(() => {
  if (customElements.get('kushae-product-information')) return;

  function initialize(section, signal) {
    const tabs = [...section.querySelectorAll('.kpi__tab')];
    const toggles = [...section.querySelectorAll('.kpi__toggle')];
    const panels = [...section.querySelectorAll('.kpi__panel')];
    const navigation = section.querySelector('.kpi__tabs');
    const mobile = window.matchMedia('(max-width: 767px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let active = 0;
    let focusedControl = null;
    // Mobile accordion items open/close independently of each other and of
    // the desktop tab selection, so they get their own tracked state.
    const openPanels = new Set([0]);
    const animations = panels.map(() => null);
    const transitionCounters = panels.map(() => 0);

    section.addEventListener('focusin', event => {
      focusedControl = tabs.includes(event.target) || toggles.includes(event.target) ? event.target : null;
    }, { signal });
    section.addEventListener('focusout', event => {
      // A breakpoint can hide the focused heading before matchMedia fires.
      // Retain it only for that transition, not for an ordinary blur.
      if (event.relatedTarget || event.target.getClientRects().length) focusedControl = null;
    }, { signal });

    function revealTab() {
      const tab = tabs[active].getBoundingClientRect();
      const track = navigation.getBoundingClientRect();
      const delta = tab.left < track.left ? tab.left - track.left
        : tab.right > track.right ? tab.right - track.right : 0;
      if (delta) navigation.scrollBy({ left: delta, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    function showSelected() {
      panels.forEach((panel, i) => {
        panel.hidden = mobile.matches ? !openPanels.has(i) : i !== active;
        panel.inert = false;
        panel.scrollTop = 0;
      });
    }

    function cancelAnimation(index) {
      transitionCounters[index]++;
      animations[index]?.cancel();
      animations[index] = null;
    }

    // Constant expansion speed (px/ms) so tall and short content open at the
    // same visual rate instead of both fitting into one fixed duration.
    const ACCORDION_PX_PER_MS = 0.7;
    const ACCORDION_MIN_MS = 180;
    const ACCORDION_MAX_MS = 520;

    async function animatePanel(panel, opening, index) {
      // Grid animates natural mobile height without measuring or fixing its size.
      // Desktop fades only the copy, leaving its 258px bordered panel stationary.
      const target = mobile.matches ? panel.parentElement : panel.querySelector('.kpi__content');
      const expanded = mobile.matches ? { gridTemplateRows: '1fr', opacity: 1 } : { opacity: 1, transform: 'translateY(0)' };
      const collapsed = mobile.matches ? { gridTemplateRows: '0fr', opacity: 0 } : { opacity: 0, transform: 'translateY(6px)' };
      let duration = opening ? 240 : 160;
      if (mobile.matches) {
        const height = panel.querySelector('.kpi__content').scrollHeight;
        const base = Math.min(ACCORDION_MAX_MS, Math.max(ACCORDION_MIN_MS, height / ACCORDION_PX_PER_MS));
        duration = opening ? base : base * 0.7;
      }
      const effect = target.animate(opening ? [collapsed, expanded] : [expanded, collapsed], {
        duration,
        easing: opening ? 'cubic-bezier(0.2, 0.7, 0.2, 1)' : 'ease-in',
        fill: 'both'
      });
      animations[index] = effect;
      try { await effect.finished; } catch { /* A new selection or resize superseded this animation. */ }
      effect.cancel();
      if (animations[index] === effect) animations[index] = null;
    }

    // Desktop tab selection: exclusive, exactly one panel visible at a time.
    async function select(index, reveal = false, animate = true) {
      if (index === active && animate) return;
      const outgoing = panels.find(panel => !panel.hidden);
      const outgoingIndex = panels.indexOf(outgoing);
      if (outgoingIndex !== -1) cancelAnimation(outgoingIndex);
      cancelAnimation(index);
      const currentTransition = transitionCounters[index];
      active = index;
      tabs.forEach((tab, i) => {
        const selected = i === active;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (reveal && !mobile.matches) revealTab();
      if (!animate || reducedMotion.matches || !outgoing || !outgoing.animate) {
        showSelected();
        return;
      }
      outgoing.inert = true;
      await animatePanel(outgoing, false, outgoingIndex);
      if (currentTransition !== transitionCounters[index]) return;
      showSelected();
      await animatePanel(panels[active], true, index);
    }

    // Mobile accordion: each item toggles independently and stays open
    // until it is clicked again, regardless of what else is open.
    async function toggleAccordion(index) {
      const opening = !openPanels.has(index);
      const toggle = toggles[index];
      const panel = panels[index];
      cancelAnimation(index);
      const currentTransition = transitionCounters[index];
      if (opening) openPanels.add(index); else openPanels.delete(index);
      toggle.setAttribute('aria-expanded', String(opening));
      toggle.querySelector('.kpi__icon').textContent = opening ? '−' : '+';
      if (opening) {
        panel.hidden = false;
        panel.inert = false;
      }
      if (!panel.animate || reducedMotion.matches) {
        if (!opening) panel.hidden = true;
        return;
      }
      if (!opening) panel.inert = true;
      await animatePanel(panel, opening, index);
      if (currentTransition !== transitionCounters[index]) return;
      if (!opening) panel.hidden = true;
      else panel.inert = false;
    }

    function syncAccordionUI() {
      showSelected();
      toggles.forEach((toggle, i) => {
        const open = openPanels.has(i);
        toggle.setAttribute('aria-expanded', String(open));
        toggle.querySelector('.kpi__icon').textContent = open ? '−' : '+';
      });
    }

    function updateMode() {
      const focusedTab = tabs.indexOf(focusedControl);
      const focusedToggle = toggles.indexOf(focusedControl);
      panels.forEach((panel, i) => {
        panel.setAttribute('role', mobile.matches ? 'region' : 'tabpanel');
        panel.setAttribute('aria-labelledby', mobile.matches ? toggles[i].id : tabs[i].id);
        if (mobile.matches) panel.removeAttribute('tabindex');
        else panel.tabIndex = 0;
      });
      if (mobile.matches && focusedTab !== -1) toggles[active].focus({ preventScroll: true });
      if (!mobile.matches && focusedToggle !== -1) tabs[active].focus({ preventScroll: true });
      if (mobile.matches) {
        // Carry the desktop-selected tab over as open if nothing is open yet.
        if (openPanels.size === 0) openPanels.add(active);
        syncAccordionUI();
      } else {
        select(active, true, false);
      }
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index, true), { signal });
      tab.addEventListener('keydown', event => {
        let next;
        const direction = getComputedStyle(navigation).direction === 'rtl' ? -1 : 1;
        if (event.key === 'ArrowRight') next = (index + direction + tabs.length) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - direction + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        tabs[next].focus({ preventScroll: true });
        select(next, true);
      }, { signal });
      toggles[index].addEventListener('click', () => toggleAccordion(index), { signal });
    });
    mobile.addEventListener('change', updateMode, { signal });
    reducedMotion.addEventListener('change', () => {
      if (mobile.matches) syncAccordionUI(); else select(active, false, false);
    }, { signal });
    signal.addEventListener('abort', () => {
      panels.forEach((_, i) => cancelAnimation(i));
      showSelected();
    }, { once: true });
    updateMode();
  }

  // The same lifecycle as the main product section handles Theme Editor reloads.
  customElements.define('kushae-product-information', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      initialize(this, this.controller.signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });
})();